import React, { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  SortingState,
  ColumnFiltersState,
} from "@tanstack/react-table";
import { Loader2, AlertTriangle } from "lucide-react";
import { useQueryState, useQueryStates, parseAsString, createParser } from 'nuqs';
import { PaginatedResponse, ListQueryParams } from "@/app/types/api";
import { Party } from "@/app/types/party";
import { listParties } from "@/app/services/partyService";
import { useAuthStore } from "@/app/store/authStore";
import { columns as baseColumns } from "./components/columns";
import PartyDataTable from "./components/PartyDataTable";

// Define selectors for auth store
const selectUser = (state: { user: any }): any => state.user;

// Custom parser for sorting state
const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return []
    const [field, direction] = value.split('-')
    return [{
      id: field,
      desc: direction === 'desc'
    }]
  },
  serialize: (value: SortingState) => {
    if (!value.length) return ''
    const sort = value[0]
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`
  }
})

// Custom parser for page (0-based internal, 1-based in URL)
const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10)
    return isNaN(page) || page < 1 ? 0 : page - 1 // Convert 1-based to 0-based
  },
  serialize: (value: number) => {
    return String(value + 1) // Convert 0-based to 1-based
  }
})

/**
 * My Parties Page (Client Manager)
 *
 * Displays list of parties assigned to the current client_manager
 * Backend filters parties where responsible_party_id matches the manager's party
 *
 * Features:
 * - TanStack Table with server-side pagination, sorting, filtering
 * - URL state management with nuqs
 * - Filter by name, phone, email, party type
 * - Sort by any column
 * - Navigate to party detail page
 */
const MyPartiesPage = () => {
  const navigate = useNavigate();

  // Use nuqs for URL state management
  const [page, setPage] = useQueryState('page', pageParser.withDefault(0))
  const [sorting, setSorting] = useQueryState('sortBy', sortingParser.withDefault([]))

  // Use nuqs for filter state management
  const [filterStates, setFilterStates] = useQueryStates({
    name: parseAsString,
    phone: parseAsString,
    email: parseAsString,
    party_type: parseAsString,
  }, {
    // Use shallow routing to prevent full page reloads
    shallow: true,
  })

  // Build columnFilters from nuqs state for compatibility with existing code
  const columnFilters = useMemo<ColumnFiltersState>(() => {
    const filters: ColumnFiltersState = [];
    Object.entries(filterStates).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        filters.push({ id: key, value });
      }
    });
    return filters;
  }, [filterStates]);

  const [pageSize] = useState(10);
  const pagination = {
    pageIndex: page,
    pageSize,
    pageCount: 0
  };

  // Auth state
  const user = useAuthStore(selectUser);

  // Get organization ID from user data
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user]);

  // Construct query parameters for the API
  const queryParams = useMemo<ListQueryParams>(() => {
    const params: ListQueryParams = {
      skip: page * pageSize,
      limit: pageSize,
    };

    // Add sorting if defined
    if (sorting.length > 0) {
      params.sort_by = sorting[0].id;
      params.sort_dir = sorting[0].desc ? "desc" : "asc";
    }

    // Add filters if defined
    if (columnFilters.length > 0) {
      const filtersList: any[] = [];
      columnFilters.forEach((filter) => {
        if (filter.value !== undefined && filter.value !== null && filter.value !== '') {
          // Use icontains for text fields to enable wildcard search
          const textFields = ['name', 'phone', 'email'];
          const operator = textFields.includes(filter.id) ? 'icontains' : 'eq';

          filtersList.push({
            field: filter.id,
            op: operator,
            value: filter.value
          });
        }
      });

      if (filtersList.length > 0) {
        params.filters = JSON.stringify(filtersList);
      }
    }

    return params;
  }, [page, pageSize, sorting, columnFilters]);

  // Query to fetch assigned parties
  const {
    data: partiesResponse = { items: [], total: 0 },
    isLoading: isPartiesLoading,
    isError: isPartiesError,
    error: partiesError,
    refetch: refetchParties,
  } = useQuery<PaginatedResponse<Party>>({
    queryKey: ['myParties', userOrgId, queryParams],
    queryFn: () => {
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      // Note: Backend will filter parties by responsible_party_id for client_manager role
      return listParties(queryParams, userOrgId);
    },
    enabled: !!userOrgId,
  });

  // Calculate page count from response
  const pageCount = useMemo(() => {
    if (partiesResponse) {
      return Math.ceil(partiesResponse.total / pageSize);
    }
    return 0;
  }, [partiesResponse, pageSize]);

  // Setup event listener for view party action
  React.useEffect(() => {
    const handleViewParty = (event: CustomEvent<Party>) => {
      navigate(`/party/detail/${event.detail.id}`);
    };

    document.addEventListener('view-party', handleViewParty as EventListener);

    return () => {
      document.removeEventListener('view-party', handleViewParty as EventListener);
    };
  }, [navigate]);

  // Event Handlers
  const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }) => {
    setPage(newPagination.pageIndex);
  };

  // Handle filter changes
  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    // Build the new filter states for nuqs
    const updates: Record<string, string | null> = {};

    // List of all possible filter keys
    const knownFilterKeys = ['name', 'phone', 'email', 'party_type'];

    // First, reset all known keys to null
    knownFilterKeys.forEach(key => {
      updates[key] = null;
    });

    // Then add the active filters
    filters.forEach(filter => {
      const value = filter.value;
      updates[filter.id] = value !== undefined && value !== null && value !== '' ? String(value) : null;
    });

    // Update filters using nuqs
    setFilterStates(updates);

    // Reset to first page when filters change
    setPage(0);
  }, [setFilterStates, setPage]);

  // Handle error state
  if (isPartiesError && !!userOrgId) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-xl font-semibold text-destructive">Error Loading Parties</p>
        <p className="text-sm text-muted-foreground">
          {partiesError instanceof Error ? partiesError.message : 'An unknown error occurred.'}
        </p>
        <button onClick={() => refetchParties()} className="text-sm underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My Parties</h1>
        <p className="text-muted-foreground mt-1">
          Customers and suppliers assigned to you
          {partiesResponse && ` · ${partiesResponse.total} total`}
        </p>
      </div>

      {/* Parties Data Table */}
      <PartyDataTable
        data={partiesResponse}
        columns={baseColumns}
        isLoading={isPartiesLoading}
        pagination={{...pagination, pageCount}}
        sorting={sorting}
        columnFilters={columnFilters}
        onPaginationChange={handlePaginationChange}
        onSortingChange={setSorting}
        onColumnFiltersChange={handleFilterChange}
      />
    </div>
  );
};

export default MyPartiesPage;
